import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSubscription = async () => {
    try {
      const [subRes, adminRes] = await Promise.all([
        axios.get(`${API}/subscription/status`),
        axios.get(`${API}/admin/check`).catch(() => ({ data: { is_admin: false } }))
      ]);
      setSubscription(subRes.data);
      setIsAdmin(adminRes.data?.is_admin || false);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(`${API}/onboarding/complete`);
      setSubscription(prev => ({ ...prev, onboarding_completed: true }));
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  const skipOnboarding = async () => {
    try {
      await axios.post(`${API}/onboarding/skip`);
      setSubscription(prev => ({ ...prev, onboarding_completed: true }));
    } catch (error) {
      console.error('Failed to skip onboarding:', error);
    }
  };


  const hasFeature = (feature) => {
    if (!subscription) return false;
    if (subscription.trial_expired && subscription.is_trial) return false;
    return subscription.features?.includes(feature);
  };

  const canCreate = (type) => {
    if (!subscription) return false;
    if (subscription.trial_expired && subscription.is_trial) return false;
    
    const limits = subscription.limits || {};
    const max = limits[`max_${type}`];
    const current = limits[`current_${type}`];
    
    if (max === -1) return true; // unlimited
    return current < max;
  };

  const refresh = () => {
    fetchSubscription();
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    subscription,
    isAdmin,
    loading,
    hasFeature,
    canCreate,
    completeOnboarding,
    skipOnboarding,
    refresh
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
